<template>
    <v-dialog
        v-model="isDialogOpen"
        content-class="v-application v-application--is-ltr"
        max-width="860px"
        :fullscreen="!$vuetify.breakpoint.mdAndUp"
    >
        <v-card id="discountCard" class="overflow-auto">
            <v-card-title>{{ dialogTitle }}</v-card-title>
            <v-card-text>
                <v-form @submit.prevent="submitForm" class="discount-form">
                    <v-text-field
                        id="discountName"
                        @input="(value) => onChange('discountName', value)"
                        :value="value.discountName"
                        outlined
                        label="Rabatt Name"
                    />
                    <v-text-field
                        id="description"
                        @input="(value) => onChange('description', value)"
                        :value="value.description"
                        outlined
                        label="Rabatt Beschreibung"
                    />
                    <div class="discountType" data-testid="discount-type">
                        <v-select
                            @input="value => onChange('discountType', value)"
                            :value="value.discountType"
                            outlined
                            label="Rabatt Typ"
                            :items="discountTypeOptions"
                            variant="outlined"
                        />
                    </div>
                    <v-text-field
                        v-if="!isFixedPrice"
                        id="discountValue"
                        @input="(value) => onChange('discountValue', value)"
                        :value="value.discountValue"
                        type="number"
                        outlined
                        label="Rabatt Wert"
                        min="0"
                    />
                    <div v-else class="fixprice-grid">
                        <v-text-field
                            v-for="field in fixPriceFields"
                            :key="field.key"
                            :id="field.key"
                            @input="(value) => onChange(field.key, value)"
                            :value="value[field.key]"
                            type="number"
                            outlined
                            :label="field.label"
                            min="0"
                        />
                    </div>
                    <div class="flatRateDiscountType" data-testid="flat-rate-discount-type">
                        <v-select
                            @input="value => onChange('flatRateDiscountType', value)"
                            :value="value.flatRateDiscountType"
                            outlined
                            label="ND Rabatt Typ"
                            :items="discountTypeOptions"
                            variant="outlined"
                        />
                    </div>
                    <v-text-field
                        v-if="!isFlatRateFixPrice"
                        id="flatRateDiscountValue"
                        @input="(value) => onChange('flatRateDiscountValue', value)"
                        :value="value.flatRateDiscountValue"
                        type="number"
                        outlined
                        label="ND Rabatt Wert"
                        min="0"
                    />
                    <div v-else class="fixprice-grid">
                        <v-text-field
                            v-for="field in flatRateFixPriceFields"
                            :key="field.key"
                            :id="field.key"
                            @input="(value) => onChange(field.key, value)"
                            :value="value[field.key]"
                            type="number"
                            outlined
                            :label="field.label"
                            min="0"
                        />
                    </div>
                    <div v-if="!isFixedPrice" class="minimum-price-section">
                        <div class="minimum-price-title">Minimum Preise</div>
                        <div class="fixprice-grid">
                            <v-text-field
                                v-for="field in minimumPriceFields"
                                :key="field.key"
                                :id="field.key"
                                @input="(value) => onChange(field.key, value)"
                                :value="value[field.key]"
                                type="number"
                                outlined
                                :label="field.label"
                                min="0"
                            />
                        </div>
                    </div>
                    <v-text-field
                        id="startDate"
                        @input="(value) => onChange('startDate', value)"
                        :value="value.startDate"
                        type="date"
                        outlined
                        label="Rabatt Start date"
                    />
                    <v-text-field
                        id="endDate"
                        @input="(value) => onChange('endDate', value)"
                        :value="value.endDate"
                        type="date"
                        outlined
                        label="Rabatt End date"
                    />
                    <v-switch
                        id="isActive"
                        label="Ist der Rabatt aktiv?"
                        v-model="value.isActive"
                    />
                    <v-card-actions class="justify-end">
                        <v-btn
                            outlined
                            color="secondary"
                            @click="closeDialog"
                        >
                            Abbrechen
                        </v-btn>
                        <v-btn
                            class="discount-btn"
                            color="success"
                            :disabled="disableDiscountCreation"
                            id="createEditDiscount"
                            density="comfortable"
                            type="submit"
                        >
                            {{ ctaTitle }}
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { discountTypeOptions, fixPriceFields, flatRateFixPriceFields, minimumPriceFields } from "../../../constants.js";
export default {
    props: {
        value: Object,
        dialogTitle: String,
        ctaTitle: String,
        isDiscountFormDialogOpen: Boolean
    },
    data: () => ({
        discountTypeOptions,
        fixPriceFields,
        flatRateFixPriceFields,
        minimumPriceFields,
        isActive: false,
    }),
    methods: {
        onChange(key, value) {
            const nextValue = {
                ...this.value, [key]: value
            };
            if (key === "discountType" && this.isFixedPriceType(value)) {
                this.minimumPriceFields.forEach((field) => {
                    nextValue[field.key] = null;
                });
            }
            this.$emit('input', nextValue)
        },
        isFixedPriceType(value) {
            return ["fixprice", "fixpreis"].includes(String(value || "").toLowerCase());
        },
        hasDiscountValue(value) {
            return value !== null && value !== undefined && value !== "";
        },
        areFixPricePairsValid(fields, requireCompletePair = false) {
            let hasCompletePair = false;
            for (let index = 0; index < fields.length; index += 2) {
                const baseRate = this.value?.[fields[index].key];
                const margin = this.value?.[fields[index + 1].key];
                const hasBaseRate = this.hasDiscountValue(baseRate);
                const hasMargin = this.hasDiscountValue(margin);
                if (hasBaseRate !== hasMargin) {
                    return false;
                }
                if (hasBaseRate && hasMargin) {
                    hasCompletePair = true;
                }
            }
            return !requireCompletePair || hasCompletePair;
        },
        submitForm() {
            this.$emit('submit', this.value);
        },
        closeDialog(){
            this.isDialogOpen = false;
        }
    },
    computed: {
        isDialogOpen: {
            get() {
                return this.isDiscountFormDialogOpen;
            },
            set(state) {
                this.$emit("update:isDiscountFormDialogOpen", state);
            },
        },
        isFixedPrice() {
            return this.isFixedPriceType(this.value?.discountType);
        },
        isFlatRateFixPrice() {
            return this.isFixedPriceType(this.value?.flatRateDiscountType);
        },
        hasRequiredDiscountValue() {
            if (this.isFixedPrice) {
                return this.areFixPricePairsValid(this.fixPriceFields, true);
            }
            return this.hasDiscountValue(this.value?.discountValue);
        },
        hasRequiredFlatRateDiscountValue() {
            if (!this.value?.flatRateDiscountType || !this.isFlatRateFixPrice) {
                return true;
            }
            return this.areFixPricePairsValid(this.flatRateFixPriceFields, true);
        },
        hasValidMinimumPrices() {
            if (this.isFixedPrice) return true
            return this.areFixPricePairsValid(this.minimumPriceFields);
        },
        disableDiscountCreation() {
            return !(
                this.value?.discountName?.trim()?.length
                && this.value?.description?.trim()?.length
                && this.value?.discountType
                && this.hasRequiredDiscountValue
                && this.hasRequiredFlatRateDiscountValue
                && this.hasValidMinimumPrices
            )
        }
    },
};
</script>

<style scoped>
.fixprice-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 12px;
}

.minimum-price-section {
    margin-top: 8px;
}

.minimum-price-title {
    font-weight: 600;
    margin-bottom: 12px;
}

@media (max-width: 600px) {
    .fixprice-grid {
        grid-template-columns: 1fr;
    }
}
</style>
